import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import { TestBadge } from '../components/ui/TestBadge';

interface DupCompany {
  id: string;
  display_name: string;
  website: string | null;
  city: string | null;
  country_code: string | null;
  company_type: string | null;
  ist_einzelfirma: boolean;
  is_test_data: boolean;
  source_marker: string | null;
  contact_count: number;
  profile_count: number;
  match_count: number;
  created_at: string;
}

interface DupGroup {
  group_key?: string;
  name_normalized?: string;
  companies: DupCompany[];
  dup_count: number;
}

const COUNTRY_OPTIONS = [
  { code: '',   label: 'Alle Länder' },
  { code: 'DE', label: 'Deutschland' },
  { code: 'AT', label: 'Österreich' },
  { code: 'CH', label: 'Schweiz' },
  { code: 'GB', label: 'UK' },
  { code: 'US', label: 'USA' },
  { code: 'NL', label: 'Niederlande' },
  { code: 'FR', label: 'Frankreich' },
  { code: 'IT', label: 'Italien' },
  { code: 'ES', label: 'Spanien' },
  { code: 'PL', label: 'Polen' },
  { code: 'LU', label: 'Luxemburg' },
];

export function DuplicatesPage() {
  const [groups, setGroups] = useState<DupGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState<string | null>(null);
  const [autoMerging, setAutoMerging] = useState(false);
  const [autoMergeResult, setAutoMergeResult] = useState<{ merged_groups: number; merged_companies: number; moved: { contacts: number; profiles: number; matches: number } } | null>(null);
  const [mode, setMode] = useState<'exact' | 'fuzzy'>('exact');
  const [threshold, setThreshold] = useState(0.85);
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [country, setCountry] = useState('');
  const [minSize, setMinSize] = useState(2);

  // Debounce-Suchfeld: 300 ms nach letztem Tastendruck
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = async () => {
    setLoading(true);
    try {
      const sp = new URLSearchParams();
      if (mode === 'fuzzy') { sp.set('mode', 'fuzzy'); sp.set('threshold', String(threshold)); }
      if (qDebounced) sp.set('q', qDebounced);
      if (country) sp.set('country', country);
      if (minSize !== 2) sp.set('min_size', String(minSize));
      const qs = sp.toString();
      const { data } = await apiClient.get(`/companies/duplicates${qs ? `?${qs}` : ''}`);
      setGroups(data.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [mode, threshold, qDebounced, country, minSize]);

  const handleAutoMerge = async () => {
    if (!confirm(`Alle ${groups.length} Duplikat-Gruppen automatisch zusammenlegen?\n\nPro Gruppe wird die Firma mit den meisten verknüpften Daten (Kontakte × 10 + Profile × 5 + Matches) zum Ziel; alle anderen werden hineingelegt und soft-deletet.`)) return;
    setAutoMerging(true);
    setAutoMergeResult(null);
    try {
      const { data } = await apiClient.post('/companies/duplicates/auto-merge');
      setAutoMergeResult(data);
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      alert(msg ?? 'Fehler beim Auto-Merge.');
    } finally { setAutoMerging(false); }
  };

  const handleMerge = async (targetId: string, sourceId: string, label: string) => {
    if (!confirm(`„${label}" wirklich zusammenlegen?\n\nAlle Kontakte, Profile, Matches und Versendungen der Quell-Firma werden auf die Ziel-Firma verschoben. Die Quelle wird danach gelöscht (soft-delete).`)) return;
    setMerging(`${targetId}:${sourceId}`);
    try {
      await apiClient.post(`/companies/${targetId}/merge`, { source_id: sourceId });
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      alert(msg ?? 'Fehler beim Zusammenlegen.');
    } finally { setMerging(null); }
  };

  // Target-Empfehlung: die Firma mit den meisten verknüpften Daten (Kontakte+Profile+Matches)
  const pickTarget = (companies: DupCompany[]): string => {
    const scored = [...companies].map(c => ({
      id: c.id,
      score: c.contact_count * 10 + c.profile_count * 5 + c.match_count,
    }));
    scored.sort((a, b) => b.score - a.score);
    return scored[0].id;
  };

  const total = groups.reduce((n, g) => n + g.dup_count, 0);
  const gesparteZeilen = groups.reduce((n, g) => n + (g.dup_count - 1), 0);

  return (
    <div style={{ padding: '24px 28px', maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 600, margin: 0 }}>Doppelte Firmen</h1>
        <div style={{ flex: 1 }} />
        <button onClick={load} disabled={loading}
          style={{
            padding: '7px 14px', background: 'var(--surface)', border: '1px solid var(--border2)',
            borderRadius: 'var(--radius)', fontSize: '12px', cursor: loading ? 'wait' : 'pointer',
          }}>
          {loading ? 'Laden…' : '↻ Neu laden'}
        </button>
        {groups.length > 0 && (
          <button onClick={handleAutoMerge} disabled={autoMerging}
            style={{
              padding: '8px 16px', background: 'var(--accent)', color: '#fff',
              border: 'none', borderRadius: 'var(--radius)',
              fontSize: '13px', fontWeight: 500,
              cursor: autoMerging ? 'wait' : 'pointer', opacity: autoMerging ? 0.7 : 1,
            }}>
            {autoMerging ? '⏳ Zusammenlegen…' : `✨ Alle ${groups.length} Gruppen automatisch zusammenlegen`}
          </button>
        )}
      </div>

      {autoMergeResult && (
        <div style={{ padding: '10px 14px', marginBottom: '14px',
          background: '#e8f5e9', color: '#2e7d32', borderRadius: 'var(--radius)', fontSize: '13px' }}>
          ✓ {autoMergeResult.merged_groups} Gruppen zusammengelegt · {autoMergeResult.merged_companies} Firmen gelöscht ·
          {' '}verschoben: {autoMergeResult.moved.contacts} Kontakte, {autoMergeResult.moved.profiles} Profile, {autoMergeResult.moved.matches} Matches
        </div>
      )}

      <div style={{ padding: '10px 14px', marginBottom: '16px',
        background: 'var(--surface2)', borderRadius: 'var(--radius)', fontSize: '12px', color: 'var(--text2)' }}>
        Firmen werden anhand eines normalisierten Namens (Kleinschreibung, ohne Rechtsform-Suffixe) gruppiert.
        {groups.length > 0 && <> Aktuell: <strong>{groups.length}</strong> Gruppen mit insgesamt <strong>{total}</strong> Einträgen. Nach Bereinigung: –{gesparteZeilen} Firmen.</>}
      </div>

      {/* Filter-Leiste */}
      <div style={{
        display: 'grid', gap: 10, marginBottom: 14,
        gridTemplateColumns: 'minmax(220px, 1fr) auto auto auto', alignItems: 'center',
      }}>
        <input
          type="search"
          placeholder={'Im Namen / Webseite / Stadt suchen (z.B. „Decker")'}
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{
            padding: '8px 12px', border: '1px solid var(--border2)',
            borderRadius: 'var(--radius)', fontSize: 13,
            background: 'var(--surface)', color: 'var(--text)',
          }}
        />
        <select
          value={country}
          onChange={e => setCountry(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: 12, background: 'var(--surface)', color: 'var(--text2)' }}
        >
          {COUNTRY_OPTIONS.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text3)' }}>
          Mind. Größe:
          <input type="number" min={2} max={50} value={minSize}
            onChange={e => setMinSize(Math.max(2, Math.min(50, parseInt(e.target.value) || 2)))}
            style={{ width: 60, padding: '6px 8px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: 12 }}
          />
        </label>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['exact', 'fuzzy'] as const).map(m => (
            <button key={m} onClick={() => setMode(m)} style={{
              padding: '6px 14px', fontSize: 12, borderRadius: 6, cursor: 'pointer',
              border: '1px solid var(--border2)',
              background: mode === m ? 'var(--accent)' : 'var(--surface)',
              color: mode === m ? '#fff' : 'var(--text2)', fontWeight: mode === m ? 600 : 400,
            }}>
              {m === 'exact' ? 'Exakt' : 'Fuzzy'}
            </button>
          ))}
        </div>
      </div>
      {mode === 'fuzzy' && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text3)', flexWrap: 'wrap' }}>
            Ähnlichkeits-Schwelle:
            <input type="range" min="0.20" max="0.99" step="0.01" value={threshold}
              onChange={e => setThreshold(parseFloat(e.target.value))}
              style={{ width: 240 }} />
            <span style={{ fontFamily: 'DM Mono, monospace', minWidth: 36 }}>{threshold.toFixed(2)}</span>
            <span style={{ color: 'var(--text3)', marginLeft: 6 }}>
              {threshold >= 0.8 ? 'fast identisch' : threshold >= 0.6 ? 'ähnlich' : threshold >= 0.4 ? 'lose' : 'sehr breit (mit Suchwort kombinieren!)'}
            </span>
          </label>
          {threshold < 0.5 && !qDebounced && (
            <div style={{ marginTop: 6, padding: '6px 10px', background: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 'var(--radius)', fontSize: 11, color: '#e65100' }}>
              {'Achtung: bei sehr niedriger Schwelle ohne Suchwort werden tausende Paare zurückgegeben — bitte ein Wort eingeben (z.B. „Decker"), um zu fokussieren.'}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text3)' }}>Laden …</div>
      ) : groups.length === 0 ? (
        <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text3)', fontSize: '13px',
          background: 'var(--surface2)', borderRadius: 'var(--radius)' }}>
          Keine Duplikate gefunden.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {groups.map(g => {
            const targetId = pickTarget(g.companies);
            const groupKey = g.group_key ?? g.name_normalized ?? g.companies[0]?.id ?? '';
            return (
              <div key={groupKey} style={{
                border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                background: 'var(--surface)', overflow: 'hidden',
              }}>
                <div style={{ padding: '10px 14px', background: 'var(--surface2)',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '12px', color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>
                    {groupKey}
                  </span>
                  <span style={{
                    padding: '2px 7px', borderRadius: '10px', fontSize: '10px',
                    background: 'var(--accent)', color: '#fff', fontWeight: 700,
                  }}>{g.dup_count} Einträge</span>
                </div>

                <div>
                  {g.companies.map(c => {
                    const isTarget = c.id === targetId;
                    return (
                      <div key={c.id} style={{
                        padding: '10px 14px',
                        borderBottom: '1px solid var(--border)',
                        background: isTarget ? '#f5fbf5' : undefined,
                        display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '12px', alignItems: 'center',
                      }}>
                        <div>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <Link to={`/companies/${c.id}`} style={{ fontSize: '13px', fontWeight: 500, color: 'var(--accent)' }}>
                              {c.display_name}
                            </Link>
                            {isTarget && (
                              <span style={{
                                padding: '1px 7px', borderRadius: '10px', fontSize: '9px',
                                background: '#2e7d32', color: '#fff', fontWeight: 700, letterSpacing: '0.5px',
                              }}>Ziel (Behalten)</span>
                            )}
                            <TestBadge show={c.is_test_data} />
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '2px' }}>
                            {c.website && <>{c.website} · </>}
                            {c.city && <>{c.city} · </>}
                            {c.company_type && <>{c.company_type} · </>}
                            {c.contact_count} Kontakte · {c.profile_count} Profile · {c.match_count} Matches
                            {c.source_marker && <> · {c.source_marker}</>}
                          </div>
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text3)' }}>
                          {new Date(c.created_at).toLocaleDateString('de-DE')}
                        </div>
                        <div>
                          {!isTarget && (
                            <button
                              onClick={() => handleMerge(targetId, c.id, `${c.display_name} → ${g.companies.find(x => x.id === targetId)?.display_name}`)}
                              disabled={merging === `${targetId}:${c.id}`}
                              style={{
                                padding: '5px 12px', fontSize: '11px', fontWeight: 500,
                                background: 'var(--accent)', color: '#fff', border: 'none',
                                borderRadius: 'var(--radius)', cursor: 'pointer',
                              }}>
                              {merging === `${targetId}:${c.id}` ? '⏳' : '→ In Ziel einlegen'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
